import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Scan, Languages, Calendar, MessageCircle, CheckCircle2, Plus, Minus, Microscope, ShieldCheck, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-2256">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-2257" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-2258">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-2259">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-2260">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-2261">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-2262" /> : <Plus className="w-4 h-4" data-id="element-2263" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-2264">
        <p className="text-gray-600 leading-relaxed" data-id="element-2265">{answer}</p>
      </div>
    </div>;
}
export function RootCanalLondonRuPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Когда нужно лечить каналы?',
    a: 'При воспалении нерва (пульпит) или инфекции в корне (периодонтит). Симптомы: сильная боль, чувствительность к горячему/холодному, отек десны, боль при накусывании или ночная боль.'
  }, {
    q: 'Больно ли лечить каналы?',
    a: 'Нет. Лечение проводится под эффективной местной анестезией, поэтому сама процедура безболезненна. После лечения возможен временный дискомфорт в течение нескольких дней.'
  }, {
    q: 'Сколько стоит лечение каналов в Лондоне?',
    a: 'Стоимость зависит от количества каналов в зубе и сложности случая. После осмотра и рентгена мы предоставляем точный план лечения и смету.'
  }, {
    q: 'Сколько визитов нужно?',
    a: 'Иногда лечение можно завершить за 1 визит. В более сложных случаях или при наличии инфекции может потребоваться 2 визита. Это определяется после диагностики.'
  }, {
    q: 'Нужна ли коронка после лечения каналов?',
    a: 'В большинстве случаев да, особенно для жевательных зубов. Зуб после удаления нерва становится хрупким, и коронка защищает его от раскола и продлевает срок службы.'
  }, {
    q: 'Можно ли спасти зуб, если сильно болит?',
    a: 'В большинстве случаев да. Современная эндодонтия позволяет спасти даже сильно разрушенные или инфицированные зубы. Чем раньше вы обратитесь, тем больше шансов сохранить зуб.'
  }, {
    q: 'Делаете ли вы срочные визиты при острой боли?',
    a: 'Да, мы понимаем, что зубную боль нельзя терпеть. Свяжитесь с нами как можно скорее — мы постараемся найти ближайшее доступное время.'
  }, {
    q: 'Где находится клиника?',
    a: '360–364 City Road, London EC1V 2PY, рядом с Angel Station (Islington).'
  }];
  // Schemas
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Root Canal Treatment',
    description: 'Endodontic treatment to save a tooth that is badly decayed or infected.',
    procedureType: 'http://schema.org/SurgicalProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  return <Layout data-id="element-2266">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-2267" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-2268" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-2269" />

      {/* 1. HERO SECTION */}
      <section className="relative bg-white overflow-hidden" data-id="element-2270">
        <div className="absolute inset-0 bg-teal-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-2271" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-2272">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-2273">
            <div className="space-y-6" data-id="element-2274">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-800 text-sm font-medium" data-id="element-2275">
                <span className="relative flex h-2 w-2" data-id="element-2276">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" data-id="element-2277"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" data-id="element-2278"></span>
                </span>
                Эндодонтия (лечение каналов)
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-2279">
                Лечение каналов в Лондоне –{' '}
                <span className="text-teal-600" data-id="element-2280">Dentico Clinic</span>
                <br data-id="element-2281" />
                <span className="text-2xl sm:text-3xl font-medium text-gray-500 mt-2 block" data-id="element-2282">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-2283">
                Безболезненное лечение корневых каналов под микроскопом. Спасаем
                зубы от удаления. Консультация на русском языке.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-gray-700" data-id="element-2284">
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-2285">
                  <Microscope className="w-4 h-4 text-teal-600" data-id="element-2286" />
                  Лечение под микроскопом
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-2287">
                  <Zap className="w-4 h-4 text-yellow-500" data-id="element-2288" />
                  Без боли
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-2289">
                  <Languages className="w-4 h-4 text-teal-600" data-id="element-2290" />
                  Русский язык
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-2291">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2292">
                  <Button size="lg" className="w-full gap-2 shadow-lg shadow-teal-600/20" data-id="element-2293">
                    <Calendar className="w-5 h-5" data-id="element-2294" />
                    Записаться на приём
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2295">
                  <Button variant="outline" size="lg" className="w-full gap-2 border-teal-200 text-teal-700 hover:bg-teal-50" data-id="element-2296">
                    <MessageCircle className="w-5 h-5 text-green-600" data-id="element-2297" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-2298">
              <div className="absolute inset-0 bg-teal-200 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-2299"></div>
              <img src="https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=1200&q=80" alt="Лечение каналов - Dentico Clinic" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full" data-id="element-2300" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHAT IS ROOT CANAL */}
      <section className="py-16 bg-white" data-id="element-2301">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2302">
          <div className="text-center mb-10" data-id="element-2303">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-2304">
              Что такое лечение каналов?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-2305">
              Лечение корневых каналов (эндодонтия) — это процедура,
              направленная на спасение зуба, который был сильно поврежден
              кариесом или инфекцией. Когда бактерии проникают внутрь зуба к
              нерву (пульпе), это вызывает воспаление и боль. Если не провести
              лечение вовремя, инфекция может распространиться на кость и
              привести к потере зуба.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mt-4" data-id="element-2306">
              Современная эндодонтия позволяет очистить каналы от инфекции,
              запломбировать их и восстановить зуб, сохраняя его
              функциональность на долгие годы. Это единственная альтернатива
              удалению зуба при глубоком поражении.
            </p>
          </div>
        </div>
      </section>

      {/* 3. PROCESS */}
      <section className="py-16 bg-gray-50" data-id="element-2307">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2308">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-2309">
            Как проходит лечение
          </h2>
          <div className="grid md:grid-cols-5 gap-4" data-id="element-2310">
            {[{
            step: '01',
            title: 'Диагностика',
            desc: 'Рентген-снимок для оценки состояния каналов.'
          }, {
            step: '02',
            title: 'Анестезия',
            desc: 'Полное обезболивание для комфортного лечения.'
          }, {
            step: '03',
            title: 'Очистка',
            desc: 'Удаление инфицированных тканей и обработка каналов.'
          }, {
            step: '04',
            title: 'Пломбирование',
            desc: 'Герметичное заполнение каналов специальным материалом.'
          }, {
            step: '05',
            title: 'Восстановление',
            desc: 'Установка пломбы или коронки для защиты зуба.'
          }].map((item, idx) => <Card key={idx} className="p-5 relative overflow-hidden h-full" data-id="element-2311">
                <div className="text-5xl font-bold text-teal-50 absolute -top-2 -right-2 select-none" data-id="element-2312">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-2313">
                  <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2314">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-2315">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 4. MODERN APPROACH */}
      <section className="py-16 bg-white" data-id="element-2316">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2317">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-2318">
            <div data-id="element-2319">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-2320">
                Современный подход к лечению
              </h2>
              <ul className="space-y-4" data-id="element-2321">
                {['Использование дентального микроскопа для точности', 'Цифровая рентген-диагностика (минимальное облучение)', 'Эффективная анестезия (лечение без боли)', 'Возможность лечения за один визит', 'Сохранение даже сложных зубов'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-2322">
                    <CheckCircle2 className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" data-id="element-2323" />
                    <span className="text-gray-700 text-lg" data-id="element-2324">{item}</span>
                  </li>)}
              </ul>
              <div className="mt-8" data-id="element-2325">
                <Link to="/ru/contact" data-id="element-2326">
                  <Button size="lg" className="gap-2" data-id="element-2327">
                    <Calendar className="w-5 h-5" data-id="element-2328" />
                    Записаться на консультацию
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl" data-id="element-2329">
              <img src="https://images.unsplash.com/photo-1609840114035-3c981b782dfe?auto=format&fit=crop&w=1200&q=80" alt="Современное лечение каналов" className="absolute inset-0 w-full h-full object-cover" data-id="element-2330" />
            </div>
          </div>
        </div>
      </section>

      {/* 5. BENEFITS */}
      <section className="py-16 bg-teal-50" data-id="element-2331">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2332">
          <div className="text-center mb-12" data-id="element-2333">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2334">
              Почему стоит лечить каналы у нас
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2335">
            {[{
            title: 'Сохранение зуба',
            desc: 'Мы боремся за каждый зуб, чтобы избежать удаления и имплантации.'
          }, {
            title: 'Без боли',
            desc: 'Современные анестетики гарантируют комфорт во время процедуры.'
          }, {
            title: 'Русский язык',
            desc: 'Вы точно поймете диагноз и план лечения.'
          }, {
            title: 'Опытные врачи',
            desc: 'Специалисты по эндодонтии, работающие со сложными случаями.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-white text-center" data-id="element-2336">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 mb-4 mx-auto" data-id="element-2337">
                  <ShieldCheck className="w-6 h-6" data-id="element-2338" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2339">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-2340">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-2341">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2342">
          <div className="text-center mb-10" data-id="element-2343">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2344">
              Частые вопросы о лечении каналов
            </h2>
          </div>
          <div className="space-y-2" data-id="element-2345">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-2346" />)}
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-2347">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2348">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2349">
            Смотрите также
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-2350">
            <Link to="/ru/russkij-stomatolog-london" data-id="element-2351">
              <Button variant="outline" size="sm" data-id="element-2352">
                Русскоязычный стоматолог
              </Button>
            </Link>
            <Link to="/ru/neotlozhnaya-stomatologiya-london" data-id="element-2353">
              <Button variant="outline" size="sm" data-id="element-2354">
                Неотложная помощь
              </Button>
            </Link>
            <Link to="/ru/services/crowns" data-id="element-2355">
              <Button variant="outline" size="sm" data-id="element-2356">
                Коронки
              </Button>
            </Link>
            <Link to="/ru/prices" data-id="element-2357">
              <Button variant="outline" size="sm" data-id="element-2358">
                Цены
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA */}
      <section className="py-16 bg-teal-900" data-id="element-2359">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-2360">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-2361">
            Не терпите зубную боль
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-2362">
            Сохраните свой зуб сегодня. Запишитесь на консультацию.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2363">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2364">
              <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-2365">
                <Calendar className="w-5 h-5" data-id="element-2366" />
                Записаться онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2367">
              <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-2368">
                <MessageCircle className="w-5 h-5" data-id="element-2369" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>;
}